import { CommonModule } from '@angular/common'
import { Component, computed, DestroyRef, effect, ElementRef, EventEmitter, inject, input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { ThemeService } from '@app/v2/shared/services/theme.service'
import { AuthService } from '@auth/services/auth.service'
import { TranslateModule } from '@ngx-translate/core'
import { Workspace } from '@workspaces/interfaces/workspace.interface'
import { catchError, debounceTime, distinctUntilChanged, of, Subject } from 'rxjs'
import { OrganizationSwitcherService } from '../../services/organization-switcher.service'
import { OrganizationCardComponent } from '../org-card/organization-card.component'

@Component({
  selector: 'app-organization-selection-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, OrganizationCardComponent, ModalComponent],
  templateUrl: './organization-selection-modal.component.html',
  styleUrls: ['./organization-selection-modal.component.scss']
})
export class OrganizationSelectionModalComponent implements OnInit, OnDestroy {
  public isOpen = input<boolean>(false)
  @Output() organizationSelected = new EventEmitter<Workspace>()
  @Output() modalClosed = new EventEmitter<void>()

  private _organizationSwitcherService = inject(OrganizationSwitcherService)
  private _themeService = inject(ThemeService)
  private _authService = inject(AuthService)
  private _modalService = inject(ModalService)
  private _destroyRef = inject(DestroyRef)
  private _searchSubject$ = new Subject<string>()
  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef

  // Signals for reactive state management
  public organizations = computed(() => this._organizationSwitcherService.organizations())
  public currentOrganization = computed(() => this._organizationSwitcherService.currentOrganization())
  public isLoading = computed(() => this._organizationSwitcherService.isLoading())
  public error = computed(() => this._organizationSwitcherService.error())
  public currentWorkspaceId = computed(() => this._currentWorkspaceId())
  public searchQuery = computed(() => this._searchQuery())
  public themeService = this._themeService
  public isLoadingMore = computed(() => this._organizationSwitcherService.isLoadingMore())
  public hasMorePages = computed(() => this._organizationSwitcherService.hasMorePages())
  public isSearching = computed(() => this._organizationSwitcherService.isSearching())

  // Use organizations directly from service (no client-side filtering)
  public filteredOrganizations = computed(() => this.organizations())
  private _searchQuery = signal<string>('')
  private _currentWorkspaceId = signal<number | null>(null)
  /**
   * Effect to handle isOpen signal changes
   * Syncs parent isOpen signal with modal service state
   */
  constructor() {
    effect(() => {
      const isCurrentlyOpen = this._modalService.isModalOpen('organization-selection-modal')

      // Only toggle if there's a mismatch between desired and actual state
      if (this.isOpen() && !isCurrentlyOpen) {
        setTimeout(() => {
          // Register and open modal with the service
          this._modalService.toggleModal('organization-selection-modal')
          // Load organizations when modal opens to show loading state
          this._loadOrganizations()
        }, 0)
      } else if (!this.isOpen() && isCurrentlyOpen) {
        setTimeout(() => {
          this._modalService.toggleModal('organization-selection-modal')
        }, 0)
      }
    })
  }
  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.setupSearchDebounce()
    this._subscribeToWorkspaceChanges()
  }
  /**
   * Checks if current view is mobile
   * @returns boolean - True if mobile view
   */
  public isMobile(): boolean {
    return window.innerWidth < 768 // md breakpoint
  }

  /**
   * Handles modal close event from modal component
   */
  public closeModalSilently(): void {
    // The modal component already toggled the service state
    // Just emit the event to notify parent to update its isOpen signal
    this.modalClosed.emit()
  }

  /**
   * Cleans up subscriptions on component destroy
   */
  ngOnDestroy(): void {
    this._searchSubject$.complete()
  }

  /**
   * Loads current workspace ID from AuthService
   * Priority: currentWorkspace > currentParentWorkspace > first available organization
   */
  private _loadCurrentWorkspaceId(): void {
    const { currentWorkspace, currentParentWorkspace } = this._authService
    const organizations = this.organizations()

    // Determine workspace ID based on priority
    const workspaceId = currentWorkspace?.id || currentParentWorkspace?.id || organizations[0]?.id

    if (workspaceId) this._currentWorkspaceId.set(workspaceId)
  }

  /**
   * Subscribes to workspace changes from AuthService
   * Updates the internal state when workspace changes externally
   */
  private _subscribeToWorkspaceChanges(): void {
    this._authService.currentWorkspaceChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((workspace) => {
      // Update the current workspace ID when it changes from external sources
      if (workspace?.id) {
        this._currentWorkspaceId.set(workspace.id)
      }
    })
  }

  /**
   * Loads organizations from the service
   */
  private _loadOrganizations(): void {
    this._searchQuery.set('')
    this._organizationSwitcherService
      .loadOrganizations()
      .pipe(
        catchError(() => {
          return of(null)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        // Load current workspace ID after organizations are loaded
        this._loadCurrentWorkspaceId()
      })
  }

  /**
   * Handles scroll events for infinite scrolling
   * @param event - Scroll event
   */
  onScroll(event: Event): void {
    const element = event.target as HTMLElement

    // Check if we're near the bottom of the scroll container
    const scrollTop = element.scrollTop
    const scrollHeight = element.scrollHeight
    const clientHeight = element.clientHeight
    const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100

    // Trigger when 80% scrolled
    if (scrollPercentage >= 80 && this.hasMorePages() && !this.isLoadingMore() && !this.searchQuery().trim()) {
      this.loadMoreOrganizations()
    }
  }

  /**
   * Loads more organizations for infinite scrolling
   */
  public loadMoreOrganizations(): void {
    if (!this.hasMorePages() || this.isLoadingMore() || this.searchQuery().trim()) {
      return // Don't load more if searching or already loading
    }

    this._organizationSwitcherService.loadMoreOrganizations().pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Handles organization selection
   * @param organization - Selected organization
   */
  public onOrganizationSelected(organization: Workspace): void {
    // Update workspace selection based on parent logic
    this.updateWorkspaceSelection(organization)

    // Switch to the selected organization
    this._organizationSwitcherService
      .switchOrganization(organization.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.organizationSelected.emit(organization)
      })
  }

  /**
   * Updates workspace selection using AuthService only
   * @param organization - Selected organization
   */
  private updateWorkspaceSelection(organization: Workspace): void {
    // Update the current workspace ID immediately for UI feedback
    this._currentWorkspaceId.set(organization.id)

    // Update last active organization - this will update localStorage AND UI state only if API succeeds
    this._authService.updateLastActiveOrganization(organization).pipe(takeUntilDestroyed(this._destroyRef)).subscribe()
  }

  /**
   * Finds the full parent organization object by name
   * @param parentName - Name of the parent organization
   * @returns Organization | null
   */
  private findParentOrganization(parentName: string): Workspace | null {
    const organizations = this.organizations()
    return organizations.find((org) => org.name === parentName) || null
  }

  /**
   * Handles search input changes with debouncing
   */
  public onSearchInputChange(value: string): void {
    this._searchQuery.set(value)
    this._searchSubject$.next(value)
  }

  /**
   * Handles Enter key press for immediate search
   */
  public onSearchKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const query = this._searchQuery()
      this.performSearch(query)
    }
  }

  /**
   * Clears the search query and resets to show all organizations
   */
  public clearSearch(): void {
    this._searchQuery.set('')
    this._searchSubject$.next('')
    this._loadOrganizations() // Reload all organizations
  }

  /**
   * Sets up debounced search functionality
   */
  private setupSearchDebounce(): void {
    this._searchSubject$
      .pipe(
        debounceTime(300), // 300ms debounce
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((query) => {
        this.performSearch(query)
      })
  }

  /**
   * Performs the actual search
   * @param query - Search query
   */
  private performSearch(query: string): void {
    if (query.trim().length >= 3 || query.trim().length === 0) {
      this._organizationSwitcherService
        .searchOrganizations(query)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(() => {
          this.scrollContainer?.nativeElement.scrollTo(0, 0)
        })
    }
  }

  /**
   * Checks if the given organization is the current one
   * @param organization - Organization to check
   * @returns boolean - True if it's the current organization
   */
  public isCurrentOrganization(organization: Workspace): boolean {
    const currentId = this.currentWorkspaceId()
    return currentId === organization.id
  }
}
